// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type StreakType = 'giving' | 'receiving' | 'posting';

export interface UserStreak {
  id: string;
  profile_id: string;
  streak_type: StreakType;
  current_streak: number;
  longest_streak: number;
  last_activity_date: string | null;
}

interface StreakUpdateResult {
  current_streak: number;
  longest_streak: number;
  milestone_reached: number;
  changed: boolean;
}

const MILESTONE_MESSAGES: Record<number, string> = {
  3: "You're on a 3-day reaction streak! Keep it going! 🔥",
  7: "🔥 7-day streak! You're on fire!",
  14: "Two weeks strong! 💪",
  30: "Legendary 30-day streak! 👑"
};

export const useUserStreaks = (profileId?: string) => {
  const [streaks, setStreaks] = useState<UserStreak[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchStreaks = useCallback(async () => {
    if (!profileId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('wall_user_streaks')
        .select('*')
        .eq('profile_id', profileId);

      if (error) {
        console.error('Error fetching streaks:', error);
        return;
      }

      setStreaks(data || []);
    } catch (err) {
      console.error('Error in useUserStreaks:', err);
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    fetchStreaks();
  }, [fetchStreaks]);

  const updateStreak = useCallback(async (streakType: StreakType): Promise<StreakUpdateResult | null> => {
    if (!profileId) return null;

    try {
      const { data, error } = await supabase.rpc('update_user_streak', {
        p_profile_id: profileId,
        p_streak_type: streakType
      });

      if (error) {
        console.error('Error updating streak:', error);
        return null;
      }

      const result = data as StreakUpdateResult;

      // Show milestone notification
      if (result.milestone_reached && result.milestone_reached > 0) {
        const message = MILESTONE_MESSAGES[result.milestone_reached];
        if (message) {
          toast.success(message, {
            duration: 5000,
          });
        }
      }

      // Refresh streaks
      await fetchStreaks();

      return result;
    } catch (err) {
      console.error('Error in updateStreak:', err);
      return null;
    }
  }, [profileId, fetchStreaks]);

  const getStreak = useCallback((type: StreakType): UserStreak | undefined => {
    return streaks.find(s => s.streak_type === type);
  }, [streaks]);

  const getBestStreak = useCallback((): UserStreak | undefined => {
    return streaks.reduce((best, current) => {
      if (!best) return current;
      return current.current_streak > best.current_streak ? current : best;
    }, undefined as UserStreak | undefined);
  }, [streaks]);

  return {
    streaks,
    loading,
    updateStreak,
    getStreak,
    getBestStreak,
    refetch: fetchStreaks
  };
};
